import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { type InlineKindEntry, type InlineRenderCtx, inlineChipProps, type ParsedInline } from '@/components/block-primitives';
import { buildHoverLabel, chipClass, collectCitationRuns, type SourceToken, serializeLocators } from '../../source-citation-shared';
import { type InlineResolution, useInlineResolution } from '../inline-resolution-context';
const parseSource = (text: string): ParsedInline[] => collectCitationRuns(text).map(run => ({
  index: run.index,
  raw: run.raw,
  token: {
    kind: 'source',
    sequence: run.sequence,
    locators: run.locators
  }
}));

// Canonical `[S#]` shorthand. A merged run (multiple locators) serializes to one marker per locator so
// it re-parses — and re-merges — back to the same token.
const serializeSource = (token: SourceToken): string => token.locators.length ? token.locators.map(locator => `[S${token.sequence} ${locator}]`).join(' ') : `[S${token.sequence}]`;

// The one source chip, drawn in prose widgets and blocks alike. A prose widget renders in a detached
// React root with no provider above it, so its registry passes the resolution in; blocks are inside the
// provider and read it from context. `data-inline-interactive` tells an editable field to leave a click
// on the chip alone, since the chip opens its own popover.
const InlineSourceChip = t0 => {
  const $ = _c(20);
  const {
    token,
    ctx,
    resolution
  } = t0;
  const {
    resolve
  } = useInlineResolution();
  let t1;
  if ($[0] !== resolution || $[1] !== resolve || $[2] !== token) {
    t1 = resolution ?? resolve(token);
    $[0] = resolution;
    $[1] = resolve;
    $[2] = token;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    source,
    state
  } = t1;
  let t2;
  if ($[4] !== ctx) {
    t2 = inlineChipProps(ctx);
    $[4] = ctx;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== source || $[7] !== token.sequence) {
    t3 = buildHoverLabel(token.sequence, source);
    $[6] = source;
    $[7] = token.sequence;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== state) {
    t4 = chipClass(state);
    $[9] = state;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== token.locators) {
    t5 = serializeLocators(token.locators);
    $[11] = token.locators;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = String(token.sequence);
  const t7 = `S${token.sequence}`;
  let t8;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <span {...t2} aria-label={t3} className={t4} data-source-chip="true" data-source-locators={t5} data-inline-interactive="true" data-source-sequence={t6} role="button" tabIndex={0}>{t7}</span>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
type GetResolve = () => InlineResolution['resolve'];

// Pass `getResolve` (a getter, so a registry built once reads the latest) for prose decorations, where the
// chip renders out of the provider's reach. Omit it and the chip resolves from context, as blocks do.
export const createSourceEntry = (getResolve?: GetResolve): InlineKindEntry => {
  const renderSourceReact = (token: SourceToken, ctx: InlineRenderCtx): ReactNode => {
    return <InlineSourceChip token={token} ctx={ctx} resolution={getResolve?.()(token)} />;
  };
  return {
    marker: '[S',
    parse: parseSource,
    serialize: serializeSource,
    renderReact: renderSourceReact,
    decorationKey: getResolve && ((token: SourceToken) => getResolve()(token).state)
  };
};

// Resolves from React context, so it is correct only where a provider sits above it: block fields.
export const contextSourceEntry = createSourceEntry();